/**
 * F-90 import UI state — one store per open board view, owned by BoardScreen.
 * Same pattern as the comments/selection stores: the top bar's "Import script"
 * button and the canvas's drop layer are different components, and both need to
 * drive the same dialog.
 *
 * It also owns the F-16 LARGE PASTE (`pasting`), which is the same operation by
 * another entry point — the grid pastes a script through the same bulk
 * machinery (`planChunks` + `insertRows` + `beginUndoGroup`). Sharing one store
 * is what makes the two mutually exclusive: an import and a paste interleaving
 * their chunks would write from each other's stale row plan and collapse into
 * one undo item.
 *
 * LOCAL view state only: the rows land in the Yjs doc through `BoardSession`
 * (`import-script.ts`), never from here.
 */
import { createStore, type StoreApi } from "zustand/vanilla";

import type { ImportPreview } from "./script-file.js";

/**
 * The import's one-way lifecycle:
 *
 *   idle → reading (file → text) → confirm (parsed; the user decides)
 *        → running (chunked writes + progress bar) → idle
 *
 * `error` holds a message the user must dismiss (an unreadable file, a file
 * type we don't import). Reading is its own phase because a 10 MB script takes
 * a beat to decode and the dialog must not appear empty while it does.
 */
export type ImportPhase =
  | { kind: "idle" }
  | { kind: "reading"; filename: string }
  | { kind: "confirm"; filename: string; preview: ImportPreview }
  | { kind: "running"; filename: string; done: number; total: number }
  /**
   * F-16 large paste, mid-write. No confirm phase: the user already chose by
   * pressing Ctrl+V, and the clipboard is the preview. It is a phase rather
   * than grid-local state so it renders the same modal progress bar as an
   * import — and so `importBusy` covers BOTH, which is the mutual exclusion.
   */
  | { kind: "pasting"; done: number; total: number }
  | { kind: "error"; message: string };

export interface ImportUiState {
  phase: ImportPhase;
  setPhase(phase: ImportPhase): void;
  reset(): void;
}

export type ImportUiStore = StoreApi<ImportUiState>;

export function createImportUiStore(): ImportUiStore {
  return createStore<ImportUiState>((set) => ({
    phase: { kind: "idle" },
    setPhase: (phase) => set({ phase }),
    reset: () => set({ phase: { kind: "idle" } }),
  }));
}

/**
 * A running bulk write must not be interrupted by a second one — an import, a
 * paste, or one of each. Both grow the sheet from a row plan captured before
 * their first `await`, and both hold ONE undo group open across their chunks,
 * so two overlapping runs would write into each other's rows from a stale plan
 * and then collapse into a single undo item that reverts both.
 */
export function importBusy(phase: ImportPhase): boolean {
  return phase.kind === "reading" || phase.kind === "running" || phase.kind === "pasting";
}
